import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigationType, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ListingDetail from './pages/ListingDetail';
import Checkout from './pages/Checkout';
import OrderConfirmation from './pages/OrderConfirmation';
import TrackOrder from './pages/TrackOrder';
import MyOrders from './pages/MyOrders';
import SellerProfile from './pages/SellerProfile';
import StorePage from './pages/StorePage';
import Messages from './pages/Messages';
import SellerSettings from './pages/SellerSettings';
import StripeAppReturn from './pages/StripeAppReturn';
import SellOnApp from './pages/SellOnApp';
import SellerLocalListingsLanding from './pages/SellerLocalListingsLanding';
import DownloadApp from './pages/DownloadApp';
import DownloadSellerApp from './pages/DownloadSellerApp';
import PayoutSettings from './pages/PayoutSettings';
import HelpCenter from './pages/HelpCenter';
import MoneyBackGuarantee from './pages/MoneyBackGuarantee';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CookiePolicy from './pages/CookiePolicy';
import Survey from './pages/Survey';
import Cart from './pages/Cart';
import Search from './pages/Search';
import Followed from './pages/Followed';
import CreateListing from './pages/CreateListing';
import MyListings from './pages/MyListings';
import MySales from './pages/MySales';
import MyStores from './pages/MyStores';
import './App.css';
import { useAuth } from './contexts/AuthContext';
import { recordPageVisit } from './utils/pageVisitTracking';
import MobileBuyerAppBanner from './components/MobileBuyerAppBanner';

const AdminDashboard = lazy(() => import('./admin'));

// Scroll to top on forward navigation; Home restores its own scroll on back.
const ScrollToTop: React.FC = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const { pathname, search, key } = location;

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    if (pathname === '/' && navigationType === 'POP') {
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pathname, search, key, navigationType]);

  useEffect(() => {
    recordPageVisit(pathname, search);
  }, [pathname, search]);

  return null;
};

// App content that needs Router context
const AppContent: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const isAdminRoute = location.pathname === '/admin';
  const isDownloadRoute = location.pathname === '/app';
  const showSiteChrome = !isAdminRoute && !isDownloadRoute;

  const showMobileBuyerBanner = showSiteChrome && location.pathname === '/';

  return (
    <div
      className={
        isAdminRoute
          ? 'App flex h-screen flex-col overflow-hidden'
          : `App flex min-h-screen flex-col ${isAuthenticated ? 'pb-32 sm:pb-0' : ''}`
      }
    >
      <ScrollToTop />
      {showMobileBuyerBanner ? <MobileBuyerAppBanner /> : null}
      {!showSiteChrome ? null : <Navbar />}
      <div className={isAdminRoute ? 'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden' : 'contents'}>
      <Routes>
            {/* Public routes */}
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/listings/:id" element={<ListingDetail />} />
            <Route path="/sellers/:id" element={<SellerProfile />} />
            <Route path="/stores/:slug" element={<StorePage />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/search" element={<Search />} />
            <Route path="/sell" element={<SellOnApp />} />
            <Route path="/sell/local-listings" element={<SellerLocalListingsLanding />} />
            <Route path="/app" element={<DownloadApp />} />
            <Route path="/sell-app" element={<DownloadSellerApp />} />
            <Route path="/help" element={<HelpCenter />} />
            <Route path="/money-back-guarantee" element={<MoneyBackGuarantee />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/cookies" element={<CookiePolicy />} />
            <Route path="/survey" element={<Survey />} />
            <Route path="/survay" element={<Survey />} />
            <Route path="/stripe-app-return" element={<StripeAppReturn />} />

            {/* Seller tools — web create + manage; /sell promotes the seller app */}
            <Route path="/create" element={<ProtectedRoute><CreateListing /></ProtectedRoute>} />
            <Route path="/create/:id" element={<ProtectedRoute><CreateListing /></ProtectedRoute>} />
            <Route path="/my-sales" element={<ProtectedRoute><MySales /></ProtectedRoute>} />
            <Route path="/my-listings" element={<ProtectedRoute><MyListings /></ProtectedRoute>} />
            <Route path="/my-stores" element={<ProtectedRoute><MyStores /></ProtectedRoute>} />
            <Route path="/dashboard" element={<Navigate to="/my-listings" replace />} />
            <Route path="/payout-settings" element={<ProtectedRoute><PayoutSettings /></ProtectedRoute>} />
            
            {/* Protected routes - require login */}
            <Route path="/followed" element={<ProtectedRoute><Followed /></ProtectedRoute>} />
            <Route path="/checkout/:id?" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
            <Route path="/order-confirmation" element={<ProtectedRoute><OrderConfirmation /></ProtectedRoute>} />
            <Route path="/track-order/:orderId" element={<ProtectedRoute><TrackOrder /></ProtectedRoute>} />
            <Route path="/my-orders" element={<ProtectedRoute><MyOrders /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SellerSettings /></ProtectedRoute>} />
            <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
            <Route path="/admin" element={
              <Suspense fallback={
                <div className="flex h-screen items-center justify-center bg-gray-50 text-sm text-gray-500">
                  Loading admin…
                </div>
              }>
                <AdminDashboard />
              </Suspense>
            } />
      </Routes>
      </div>
      {!showSiteChrome ? null : <Footer />}
      <Toaster 
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <Router>
          <AppContent />
        </Router>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
